import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchTeam, fetchTeamGames, fetchTeamStats } from "../../../services/team-service";
import { STAT_TYPES, EXTRA_STAT_TYPES } from "../constants";

const enrichStats = (stats) => {
  if (!Array.isArray(stats)) return [];
  return stats.map((stat) => {
    const enriched = { ...stat };
    if (stat.year && !stat.Year) enriched.Year = stat.year;
    if (stat.Season && !stat.year) enriched.year = stat.Season;
    return enriched;
  });
};

const createInitialStatData = () =>
  EXTRA_STAT_TYPES.reduce((acc, type) => ({ ...acc, [type]: [] }), {});

export const useTeamData = (teamId) => {
  const [teamData, setTeamData] = useState(null);
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderReady, setIsHeaderReady] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(STAT_TYPES.BATTING);
  const [statData, setStatData] = useState(createInitialStatData);

  const fetchAllStats = useCallback(async () => {
    const results = await Promise.all(
      EXTRA_STAT_TYPES.map(async (type) => {
        try {
          const response = await fetchTeamStats(teamId, type);
          const sorted = enrichStats(response).sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
          return { [type]: sorted };
        } catch {
          return { [type]: [] };
        }
      })
    );
    setStatData(results.reduce((acc, result) => ({ ...acc, ...result }), {}));
  }, [teamId]);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setIsLoading(true);
      setError(null);
      setTeamData(null);
      setGames([]);
      setStatData(createInitialStatData());
      setActiveTab(STAT_TYPES.BATTING);
      setIsHeaderReady(false);

      try {
        const [teamResponse, gamesResponse] = await Promise.all([
          fetchTeam(teamId, controller.signal),
          fetchTeamGames(teamId, controller.signal).catch(() => []),
        ]);
        if (controller.signal.aborted) return;

        setTeamData(teamResponse);
        setGames(gamesResponse || []);
        await fetchAllStats();
      } catch (e) {
        if (e?.name !== "AbortError") {
          setError(e?.message || "Failed to load team data");
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [teamId, fetchAllStats]);

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (teamData?.batting_stats?.length > 0) tabs.push({ id: STAT_TYPES.BATTING, label: "Team Batting" });
    if (teamData?.pitching_stats?.length > 0) tabs.push({ id: STAT_TYPES.PITCHING, label: "Team Pitching" });
    if (teamData?.batting_stats?.length > 0) tabs.push({ id: STAT_TYPES.LINEUP, label: "Lineup" });
    if (teamData?.pitching_stats?.length > 0) tabs.push({ id: STAT_TYPES.BULLPEN, label: "Bullpen" });
    if (teamData?.roster?.length > 0) tabs.push({ id: STAT_TYPES.ROSTER, label: "Roster" });
    if (games?.length > 0) tabs.push({ id: STAT_TYPES.GAMES, label: "Games" });
    if (statData[STAT_TYPES.VALUE_BATTING]?.length > 0) tabs.push({ id: STAT_TYPES.VALUE_BATTING, label: "Value (Batting)" });
    if (statData[STAT_TYPES.VALUE_PITCHING]?.length > 0) tabs.push({ id: STAT_TYPES.VALUE_PITCHING, label: "Value (Pitching)" });
    if (statData[STAT_TYPES.SITUATIONAL_BATTING]?.length > 0) tabs.push({ id: STAT_TYPES.SITUATIONAL_BATTING, label: "Situational (Batting)" });
    if (statData[STAT_TYPES.SITUATIONAL_PITCHING]?.length > 0) tabs.push({ id: STAT_TYPES.SITUATIONAL_PITCHING, label: "Situational (Pitching)" });
    if (statData[STAT_TYPES.SPLITS_BATTING]?.length > 0) tabs.push({ id: STAT_TYPES.SPLITS_BATTING, label: "Splits (Batting)" });
    if (statData[STAT_TYPES.SPLITS_PITCHING]?.length > 0) tabs.push({ id: STAT_TYPES.SPLITS_PITCHING, label: "Splits (Pitching)" });
    if (statData[STAT_TYPES.BATTED_BALL_BATTING]?.length > 0) tabs.push({ id: STAT_TYPES.BATTED_BALL_BATTING, label: "Batted Ball (Batting)" });
    if (statData[STAT_TYPES.BATTED_BALL_PITCHING]?.length > 0) tabs.push({ id: STAT_TYPES.BATTED_BALL_PITCHING, label: "Batted Ball (Pitching)" });
    if (statData[STAT_TYPES.BASERUNNING]?.length > 0) tabs.push({ id: STAT_TYPES.BASERUNNING, label: "Baserunning" });
    return tabs;
  }, [teamData, games, statData]);

  return {
    teamData,
    games,
    isLoading,
    isHeaderReady,
    setIsHeaderReady,
    error,
    activeTab,
    setActiveTab,
    statData,
    availableTabs,
  };
};
